import React from 'react'
import { Box, Typography, ToggleButtonGroup, ToggleButton, Tooltip } from '@mui/material'
import {
  GuidedToggleState,
  GuidedFormat,
  GuidedLength,
  GuidedAudience,
  GUIDED_LENGTH_LABELS,
  GUIDED_AUDIENCE_LABELS,
} from './wizardHelpers'

export interface GuidedTogglesProps {
  value: GuidedToggleState
  onChange: (next: GuidedToggleState) => void
}

/**
 * "Bullets or prose? How long? Who's the audience?" guided questions (#970
 * Part A, Step 2). Selections auto-inject a guidance sentence into the
 * instructions text — see `applyGuidedToggles` in wizardHelpers.ts.
 */
export const GuidedToggles: React.FC<GuidedTogglesProps> = ({ value, onChange }) => {
  const setFormat = (_e: React.MouseEvent, next: GuidedFormat | null) => onChange({ ...value, format: next ?? undefined })
  const setLength = (_e: React.MouseEvent, next: GuidedLength | null) => onChange({ ...value, length: next ?? undefined })
  const setAudience = (_e: React.MouseEvent, next: GuidedAudience | null) => onChange({ ...value, audience: next ?? undefined })

  // #1176 Surface 2: ONE horizontal row for the three short groups (caption
  // above its buttons), not six stacked rows — the step read as a scroll. The
  // italic length disclaimer moved into a tooltip on that group's caption.
  // flexWrap keeps a small window usable: groups fold under, never clip.
  return (
    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Bullet points or flowing prose?
        </Typography>
        <ToggleButtonGroup size="small" exclusive value={value.format ?? null} onChange={setFormat} color="info">
          <ToggleButton value="bullets">Bullet points</ToggleButton>
          <ToggleButton value="prose">Flowing prose</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box>
        <Tooltip title="We'll do our best — AI output length varies, so the labels say roughly.">
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Roughly how long?
          </Typography>
        </Tooltip>
        <ToggleButtonGroup size="small" exclusive value={value.length ?? null} onChange={setLength} color="info">
          {(Object.keys(GUIDED_LENGTH_LABELS) as GuidedLength[]).map(k => (
            <ToggleButton key={k} value={k}>{GUIDED_LENGTH_LABELS[k]}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Who is the audience?
        </Typography>
        <ToggleButtonGroup size="small" exclusive value={value.audience ?? null} onChange={setAudience} color="info">
          {(Object.keys(GUIDED_AUDIENCE_LABELS) as GuidedAudience[]).map(k => (
            <ToggleButton key={k} value={k}>{GUIDED_AUDIENCE_LABELS[k]}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
    </Box>
  )
}

export default GuidedToggles
